<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Share2 } from 'lucide-vue-next'
import { getCardStyle } from '../../config/cardStyles.js'
import { getExplorerUrl } from '../../config/currencies.js'
import CardMenu from './CardMenu.vue'
import MnemonicModal from './MnemonicModal.vue'
import ShareOptionsModal from './ShareOptionsModal.vue'

const { t: $t } = useI18n()

const props = defineProps({
  mode: {
    type: String,
    default: 'customize', // 'customize' or 'active'
    validator: (value) => ['customize', 'active'].includes(value)
  },
  balance: {
    type: String,
    default: '0.0000'
  },
  currency: {
    type: Object,
    required: true
  },
  bgStyle: {
    type: Number,
    default: 0
  },
  message: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  },
  isRefreshing: {
    type: Boolean,
    default: false
  },
  walletAddress: {
    type: String,
    default: null
  },
  networkId: {
    type: String,
    default: null
  },
  mnemonic: {
    type: String,
    default: null
  },
  claimUrl: {
    type: String,
    default: ''
  },
  emojiPin: {
    type: String,
    default: ''
  },
  currentStep: {
    type: Number,
    default: 1
  }
})

const emit = defineEmits(['update:bgStyle', 'update:message', 'refresh'])

// Auto-refresh interval
let refreshInterval = null

onMounted(() => {
  if (props.mode === 'active') {
    // Auto-refresh every 10 seconds
    refreshInterval = setInterval(() => {
      if (!props.loading) {
        emit('refresh')
      }
    }, 10000)
  }
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})

// Get current card style from centralized config
const currentStyle = computed(() => {
  return getCardStyle(props.bgStyle)
})

// Get display name for currency (simplified version)
const displayCurrency = computed(() => {
  const code = props.currency.code || 'BTC'
  // Convert wrapped/bridged tokens to simple names
  const displayMap = {
    'cbBTC': 'BTC',
    'WBTC': 'BTC',
    'USDC': 'USDC',
    'USDT': 'USDT',
    'ETH': 'ETH',
    'BNB': 'BNB'
  }
  return displayMap[code] || code
})

// Get current background (WebP → JPEG → gradient fallback)
const currentBackground = computed(() => {
  const style = currentStyle.value

  // Prefer WebP (smaller, better compression), fallback to JPEG, then gradient
  if (style.imageWebP) {
    return `url(${style.imageWebP}), url(${style.image}), ${style.background}`
  }

  // If only JPEG image is specified, use it with gradient fallback
  if (style.image) {
    return `url(${style.image}), ${style.background}`
  }

  // Otherwise, use gradient only
  return style.background
})

const localMessage = computed({
  get: () => props.message,
  set: (value) => emit('update:message', value)
})

const handleBalanceClick = () => {
  if (!props.loading && props.mode === 'active') {
    emit('refresh')
  }
}

// Mnemonic modal state
const showMnemonicModal = ref(false)

const handleShowMnemonic = () => {
  showMnemonicModal.value = true
}

const closeMnemonicModal = () => {
  showMnemonicModal.value = false
}

// Share modal state
const showShareModal = ref(false)

const handleOpenShareModal = () => {
  showShareModal.value = true
}

const closeShareModal = () => {
  showShareModal.value = false
}

// Explorer URL
const explorerUrl = computed(() => {
  if (!props.networkId) return null
  return getExplorerUrl(props.networkId)
})

// Step-based element opacity and animation control
const balanceOpacity = computed(() => {
  // Step 1: bright (amount selection), Step 2: muted, Step 3: bright (real balance)
  return props.currentStep === 1 || props.currentStep === 3 ? 1.0 : 0.7
})

const tickerOpacity = computed(() => {
  // Step 1: bright, Step 2-3: bright (1.0)
  return 1.0
})

const messageOpacity = computed(() => {
  // Always bright
  return 1.0
})

const shouldPulseBalance = computed(() => {
  // Pulse on step 1 (amount selection)
  return props.currentStep === 1
})

const shouldPulseTicker = computed(() => {
  // No pulse for ticker
  return false
})

const shouldPulseMessage = computed(() => {
  // Pulse on step 2 (customize message)
  return props.currentStep === 2
})

// Menu handlers
const handleRefresh = () => {
  emit('refresh')
}
</script>

<template>
  <div
    class="gift-card"
    :style="{
      background: currentBackground,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }"
    :class="{ 'is-active': mode === 'active' }"
  >
    <!-- Card Menu (Active Mode Only) -->
    <CardMenu
      v-if="mode === 'active'"
      :wallet-address="walletAddress"
      :claim-url="claimUrl"
      :emoji-pin="emojiPin"
      :explorer-url="explorerUrl"
      :has-mnemonic="!!mnemonic"
      @refresh="handleRefresh"
      @show-mnemonic="handleShowMnemonic"
    />

    <!-- Central Content: Message + Balance -->
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full flex items-center justify-center pointer-events-none">
      <div class="text-center w-full pointer-events-auto">
        <!-- Message Area (above balance) -->
        <div
          :style="{ opacity: messageOpacity, transition: 'opacity 0.4s ease' }"
          :class="{ 'pulse-animation': shouldPulseMessage }"
        >
          <!-- Customize Mode: Editable -->
          <div v-if="mode === 'customize'" class="gift-message-editable">
            <input
              v-model="localMessage"
              type="text"
              class="message-input"
              :placeholder="$t('giftStyle.messagePlaceholder', 'Happy Birthday! 🎂')"
              maxlength="50"
            />
          </div>

          <!-- Active Mode: Fixed (read-only) -->
          <div v-else-if="message" class="gift-message-fixed">
            {{ message }}
          </div>
        </div>

        <!-- Customize Mode: Muted Balance Preview -->
        <template v-if="mode === 'customize'">
          <div>
            <div
              class="balance-value-muted"
              :style="{ opacity: balanceOpacity, transition: 'opacity 0.4s ease' }"
              :class="{ 'pulse-animation': shouldPulseBalance }"
            >
              {{ balance }}
            </div>
            <div
              class="currency-ticker"
              :style="{ opacity: tickerOpacity, transition: 'opacity 0.4s ease' }"
              :class="{ 'pulse-animation': shouldPulseTicker }"
            >
              {{ displayCurrency }}
            </div>
          </div>
        </template>

        <!-- Active Mode: Real Balance (Clickable) -->
        <template v-else>
          <div
            class="balance-container"
            :class="{ 'shimmer-effect': isRefreshing }"
            @click="handleBalanceClick"
            :title="$t('walletSummary.clickToRefresh', 'Click to refresh balance')"
          >
            <div
              class="balance-value"
              :style="{ opacity: balanceOpacity, transition: 'opacity 0.4s ease' }"
              :class="{ 'pulse-animation': shouldPulseBalance }"
            >
              {{ balance }}
            </div>
            <div
              class="currency-ticker"
              :style="{ opacity: tickerOpacity, transition: 'opacity 0.4s ease' }"
              :class="{ 'pulse-animation': shouldPulseTicker }"
            >
              {{ displayCurrency }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Share Icon Button (top right, next to menu button) -->
    <button
      v-if="mode === 'active' && claimUrl && emojiPin"
      class="share-icon-button"
      @click="handleOpenShareModal"
      :title="$t('common.share', 'Share Gift Box')"
    >
      <Share2 :size="18" />
    </button>

    <!-- Card decorative elements -->
    <div class="card-shine"></div>
  </div>

  <!-- Mnemonic Modal -->
  <MnemonicModal
    v-if="mnemonic"
    :open="showMnemonicModal"
    :mnemonic="mnemonic"
    @close="closeMnemonicModal"
  />

  <!-- Share Modal -->
  <ShareOptionsModal
    v-if="mode === 'active' && claimUrl && emojiPin"
    :open="showShareModal"
    :claim-url="claimUrl"
    :emoji-pin="emojiPin"
    :wallet-address="walletAddress"
    @close="closeShareModal"
  />
</template>

<style scoped>
/* Gift Card - base styles with aspect ratio */
.gift-card {
  @apply w-full rounded-2xl p-5 flex flex-col relative shadow-md;
  aspect-ratio: 1.586 / 1;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: visible; /* Allow decorative elements and shadow to extend beyond card */
  /* Background properties set via inline style for dynamic values */
}

.gift-card.is-active {
  @apply shadow-xl;
}


/* Currency icon with scale animation */
.currency-icon {
  @apply w-12 h-12 rounded-full transition-transform duration-300;
}

.gift-card.is-active .currency-icon {
  transform: scale(1.05);
}

/* Balance Container (Clickable) */
.balance-container {
  @apply cursor-pointer;
  position: relative;
  overflow: hidden;
}

/* Shimmer Effect */
.balance-container.shimmer-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 25%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0.3) 75%,
    transparent 100%
  );
  animation: shimmer 1.5s ease-in-out;
  pointer-events: none;
}

@keyframes shimmer {
  0% {
    left: -150%;
  }
  100% {
    left: 150%;
  }
}

/* Balance Value Display */
.balance-value {
  @apply text-3xl text-black leading-none mb-1;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 900; /* Inter Black */
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.6s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Muted Balance Preview (Customize Mode) */
.balance-preview-muted {
  filter: blur(0.5px);
  opacity: 0.7;
}

.balance-value-muted {
  @apply text-3xl text-black/90 leading-none mb-1;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 900; /* Inter Black */
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Currency Ticker Display */
.currency-ticker {
  @apply text-lg text-black;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 700; /* Inter Bold */
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Share Icon Button */
.share-icon-button {
  @apply absolute top-4 right-4 z-10;
  @apply w-9 h-9 rounded-full;
  @apply bg-white/65 backdrop-blur-sm;
  @apply text-green-600;
  @apply flex items-center justify-center;
  @apply cursor-pointer transition-all duration-200;
  @apply shadow-lg;
}

.share-icon-button:hover {
  @apply bg-green-500 text-white border-green-500;
  @apply shadow-xl;
  transform: scale(1.1);
}

.share-icon-button:active {
  transform: scale(1);
}

/* Handwritten Message - Editable */
.gift-message-editable {
  @apply text-center;
  animation: fadeInMessage 0.5s ease-out;
}

.message-input {
  @apply text-lg w-full bg-transparent border-none outline-none text-black text-center;
  font-family: 'Kalam', cursive;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.5px;
}

.message-input::placeholder {
  @apply text-black;
  font-family: 'Kalam', cursive;
}

/* Handwritten Message - Fixed */
.gift-message-fixed {
  @apply text-lg text-center text-black;
  font-family: 'Kalam', cursive;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.5px;
  animation: fadeInMessage 0.5s ease-out;
}

/* Message fade-in animation */
@keyframes fadeInMessage {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Pulse animation for active elements */
@keyframes subtle-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
}

.pulse-animation {
  animation: subtle-pulse 2s ease-in-out infinite;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .balance-value {
    @apply text-[40px];
  }
}
</style>
